import { Form, FormField, SubmitButton, TextAreaInput } from 'components/Forms';
import { Heading } from 'components/Typography';
import { SignupContext, withSignupMachineState } from 'domains/signup/signupFlow/context/signupContext';
import { useCallback } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import * as yup from 'yup';

type EnterCommunicationConcernsForm = {
  communicationConcerns: string;
};

const getFormSchema = (t: TFunction): yup.ObjectSchema<EnterCommunicationConcernsForm> =>
  yup
    .object({
      communicationConcerns: yup.string().required(t('form.validation.fieldIsRequired')),
    })
    .required();

type Props = {
  defaultValues?: {
    communicationConcerns?: string;
  };
  onSubmit: SubmitHandler<EnterCommunicationConcernsForm>;
};

const CommunicationConcerns: React.FC<Props> = ({ defaultValues, onSubmit }) => {
  const { t } = useTranslation();

  return (
    <Form
      onSubmit={onSubmit}
      schema={getFormSchema(t)}
      defaultValues={defaultValues}
      validationMode="all"
      flashErrors={false}
    >
      <Heading level="h2" className="mb-64">
        {t(`signup.direct.steps.enterCommunicationConcerns.title`)}
      </Heading>

      <FormField
        data-testid="communication-concerns-input"
        component={TextAreaInput}
        name="communicationConcerns"
        disabled={false}
        label={t(`signup.direct.steps.enterCommunicationConcerns.label`)}
      />

      <SubmitButton
        data-testid="communication-concerns-submit-button"
        className="sm-d:w-full mt-64"
        size="lg"
        disableIfInvalid
        willRedirect={false}
      >
        {t('signup.direct.steps.enterCommunicationConcerns.submit')}
      </SubmitButton>
    </Form>
  );
};

const SignupWrapper = () => {
  const communicationConcerns = SignupContext.useSelector(({ context }) => context.communicationConcerns);
  const { send } = SignupContext.useActorRef();

  const onSubmit: SubmitHandler<EnterCommunicationConcernsForm> = useCallback(
    ({ communicationConcerns }) => {
      send({
        type: 'submitCommunicationConcerns',
        communicationConcerns,
      });
    },
    [send],
  );

  return (
    <CommunicationConcerns
      defaultValues={{
        communicationConcerns: communicationConcerns,
      }}
      onSubmit={onSubmit}
    />
  );
};

export const SignupEnterCommunicationConcerns = withSignupMachineState(SignupWrapper, 'enterCommunicationConcerns');
